import React, { useEffect, useRef, useState } from "react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer
} from "recharts";
import { Select } from "antd";
import { companiesService, dashboardAdmissionsService } from "./../../../redux/services";

const C_ATIVOS = "#00B6FF";
const C_DESLIG = "#E100B6";

const LegendDot = ({ color }) => (
  <span
    style={{
      display: "inline-block",
      width: 10,
      height: 10,
      borderRadius: 999,
      background: color,
      marginRight: 6
    }}
  />
);

// tooltip custom
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const a = payload.find(p => p.dataKey === "ativos");
  const d = payload.find(p => p.dataKey === "deslig");
  return (
    <div
      style={{
        background: "#fff",
        boxShadow: "0 8px 24px rgba(16,24,40,0.12)",
        borderRadius: 10,
        padding: "10px 12px",
        fontSize: 12,
        color: "#667085"
      }}
    >
      <div style={{ marginBottom: 6, fontWeight: 600, color: "#98A2B3" }}>{label}</div>
      <div style={{ display: "flex", justifyContent: "space-between", width: 140 }}>
        <span>Admissão</span>
        <span style={{ color: C_ATIVOS, fontWeight: 700 }}>{d?.value}</span>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", width: 140, marginBottom: 4 }}>
        <span>Demissão</span>
        <span style={{ color: C_DESLIG, fontWeight: 700 }}>{a?.value}</span>
      </div>
    </div>
  );
};

export default function LineStats() {
  const [emp, setEmp] = useState("all");
  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  const [loadingChart, setLoadingChart] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [modalFilter, setModalFilter] = useState(false);
  const [empDraft, setEmpDraft] = useState("");
  const [empresas, setEmpresas] = useState([
    { label: "Todas as Empresas", value: "all" },
  ]);
  const isAll = (v) => v === "" || v === "all" || v == null;

  async function carregaDados(nextEmp = emp) {
    setLoadingChart(true);
    try {
      const options = {};
      if (!isAll(nextEmp)) options.empresa = nextEmp;

      const resp = await dashboardAdmissionsService.getAll(options);

      const rotatividade =
        resp?.data?.data?.rotatividade ??
        resp?.data?.rotatividade ??
        resp?.rotatividade ??
        [];

      const arr = Array.isArray(rotatividade) ? rotatividade : [];
      const lista = arr.map((it) => ({
        competencia: String(it.competencia ?? it.competencia ?? ""),
        mes: String(it.mes ?? it.mes ?? ""),
        competencia_abrev: String(it.competencia_abrev ?? it.competencia_abrev ?? ""),
        deslig: String(it.deslig ?? it.deslig ?? ""),
        ativos: String(it.ativos ?? it.ativos ?? ""),
        value: Number(it.value ?? it.total ?? 0),
      }));

      setChartData(lista);
    } catch (e) {
      console.error("Erro ao carregar dados do gráfico", e);
      setChartData([]);
    } finally {
      setLoadingChart(false);
    }
  }

  function openFilter() {
    setEmpDraft(emp || "all");
    setModalFilter(true);
  }

  async function applyFilter() {
    const nextEmp = empDraft || "all";
    setEmp(nextEmp);
    await carregaDados(nextEmp);   // evita race com setState
    setModalFilter(false);
  }

  function cancelFilter() {
    setModalFilter(false);
  }

  async function carregaEmpresas() {
    setLoadingEmpresas(true);
    try {
      const response = await companiesService.getAll();
      const brutos = response?.data?.data ?? response?.data ?? response ?? [];
      const lista = (Array.isArray(brutos) ? brutos : []).map((d) => ({
        label: d.nome,
        value: d.uuid,
      }));
      setEmpresas((prev) => [prev[0], ...lista]);
    } catch (e) {
      console.error("Erro ao carregar empresas", e);
      setEmpresas([{ label: "Todas as empresas", value: "all" }]);
    } finally {
      setLoadingEmpresas(false);
    }
  }

  const didInitRef = useRef(false);

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    (async () => {
      await carregaEmpresas();
      await carregaDados(emp);
    })();
  }, []);

  return (
    <div
      style={{
        width: "100%",
        background: "#fff",
        padding: 16,
        height: "338px",
        position: "relative"
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8
        }}
      >
        <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
          Estatísticas de Rotatividade:
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <LegendDot color={C_ATIVOS} />
            <span style={{ color: "#667085", fontSize: 13 }}>Admissão</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <LegendDot color={C_DESLIG} />
            <span style={{ color: "#667085", fontSize: 13 }}>Demissão</span>
          </div>
        </div>
        <div
          className="filter-icon"
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            zIndex: 10
          }}
        >
          <i
            onClick={openFilter}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openFilter()}
            className="fas fa-filter"
            style={{ fontSize: 18, cursor: "pointer", color: "#00C9FF" }}
          />

          {modalFilter && (
            <div
              style={{
                position: "absolute",
                right: 0,
                top: "100%",
                marginTop: 8,
                display: "flex",
                flexDirection: "column",
                gap: 12,
                zIndex: 999,
                background: "#fff",
                borderRadius: 8,
                boxShadow: "0 8px 24px rgba(16,24,40,0.12)",
                padding: 16,
                minWidth: 240
              }}
            >
              <Select
                value={empDraft}
                onChange={setEmpDraft}
                options={empresas}
                size="middle"
                style={{ width: "100%" }}
                loading={loadingEmpresas}
              />
              <div style={{ display: "flex", gap: 8, width: "100%" }}>
                <button type="button" className="ant-btn" style={{ flex: 1 }} onClick={cancelFilter}>
                  <span>Cancelar</span>
                </button>
                <button type="button" className="ant-btn ant-btn-primary" style={{ flex: 1 }} onClick={applyFilter}>
                  <span>Filtrar</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <div style={{ width: "100%" }}>
        <div style={{ width: "100%", height: 240, overflow: "visible" }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 6, right: 20, left: 0, bottom: 8 }}>
              <CartesianGrid vertical horizontal={false} stroke="#EDEFF3" strokeOpacity={0.6} />
              <XAxis
                dataKey="mes"
                tick={{ fill: "#98A2B3", fontSize: 12 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis hide domain={["dataMin-20", "dataMax+20"]} />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ stroke: "#E8EEF5", strokeWidth: 20, opacity: 0.35 }}
              />
              <Line
                type="natural"
                dataKey="ativos"
                stroke={C_DESLIG}
                strokeWidth={4}
                dot={false}
                activeDot={{ r: 6, stroke: "#fff", strokeWidth: 3, fill: C_DESLIG }}
                animationDuration={400}
              />
              <Line
                type="natural"
                dataKey="deslig"
                stroke={C_ATIVOS}
                strokeWidth={4}
                dot={false}
                activeDot={{ r: 6, stroke: "#fff", strokeWidth: 3, fill: C_ATIVOS }}
                animationDuration={400}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      {loadingChart && (
        <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", background: "rgba(255,255,255,0.6)" }}>
          Carregando…
        </div>
      )}
    </div>
  );
}
